import { useState, useEffect, useCallback } from 'react';

const API =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  'http://127.0.0.1:5000';

const tok = () => localStorage.getItem('portalToken');

const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
const whenText = (d) => (d < 0 ? `ended ${-d} day${d === -1 ? '' : 's'} ago` : d === 0 ? 'ends today' : `ends in ${d} day${d === 1 ? '' : 's'}`);

// "Contracts expiring" panel on the Talent Pool dashboard: Placed staff whose
// contract has ended or ends soon, grouped by vehicle, with one reminder email
// per vehicle to its managers (+ any extra addresses), copied to the sender.
export default function ContractExpiryPanel({ onOpenProfile }) {
  const [groups, setGroups] = useState([]);
  const [lookahead, setLookahead] = useState(60);
  const [open, setOpen] = useState(false);
  const [extras, setExtras] = useState({});
  const [sending, setSending] = useState('');
  const [msgs, setMsgs] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/contract-expiry`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (!res.ok) return;
      const d = await res.json();
      setGroups(d.groups || []);
      setLookahead(d.lookahead_days || 60);
    } catch { /* panel just stays empty */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async (fleetNr) => {
    setSending(fleetNr); setMsgs((m) => ({ ...m, [fleetNr]: '' }));
    try {
      const res = await fetch(`${API}/api/admin/contract-expiry/remind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ fleet_nr: fleetNr, extra_emails: extras[fleetNr] || '' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send');
      setMsgs((m) => ({ ...m, [fleetNr]: { ok: true, text: d.message } }));
      setExtras((e) => ({ ...e, [fleetNr]: '' }));
      load();
    } catch (e) {
      setMsgs((m) => ({ ...m, [fleetNr]: { ok: false, text: e.message || 'Could not send' } }));
    } finally {
      setSending('');
    }
  };

  if (!groups.length) return null;

  const staff = groups.reduce((n, g) => n + g.people.length, 0);
  const ended = groups.reduce((n, g) => n + g.people.filter((p) => p.days_left < 0).length, 0);

  return (
    <div style={{ marginBottom: 20, border: '1px solid #f0c36d', borderRadius: 10, background: '#fffaf0' }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', textAlign: 'left', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#7d5200' }}>
        <b>{open ? '▾' : '▸'} 📅 Contracts expiring</b> — {staff} staff on {groups.length} vehicle{groups.length === 1 ? '' : 's'}
        {ended > 0 && <span style={{ color: '#c0392b' }}> ({ended} already ended)</span>}
        <span style={{ color: '#a07a2c', fontSize: 12 }}> · ended or ending within {lookahead} days</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 12px' }}>
          {groups.map((g) => {
            const msg = msgs[g.fleet_nr];
            return (
              <div key={g.fleet_nr || 'none'} style={{ marginTop: 10, padding: '12px 14px', background: '#fff', border: '1px solid #f3dfb5', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#2c1e3a' }}>🚐 {g.vehicle}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
                  <tbody>
                    {g.people.map((p) => (
                      <tr key={p.applicant_id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '4px 6px' }}>
                          <button type="button" onClick={() => onOpenProfile && onOpenProfile(p.applicant_id)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#1565c0', cursor: 'pointer', fontSize: 13 }}>{p.name}</button>
                        </td>
                        <td style={{ padding: '4px 6px', color: '#666' }}>{p.position || '—'}</td>
                        <td style={{ padding: '4px 6px' }}>{fmtDate(p.end_date)}</td>
                        <td style={{ padding: '4px 6px', color: p.days_left < 0 ? '#c0392b' : '#666' }}>{whenText(p.days_left)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12.5, color: '#555', marginTop: 8 }}>
                  <b>Goes to:</b>{' '}
                  {g.managers.length ? g.managers.map((m) => m.name || m.email).join(', ') : <i>no manager covers this vehicle — add an email below</i>}
                  {' '}+ copy to you
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <input value={extras[g.fleet_nr] || ''} onChange={(e) => setExtras((x) => ({ ...x, [g.fleet_nr]: e.target.value }))}
                    placeholder="Extra emails (optional), e.g. natacha@crescer.group"
                    style={{ flex: 1, minWidth: 220, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
                  <button type="button" onClick={() => send(g.fleet_nr)} disabled={sending === g.fleet_nr}
                    style={{ padding: '7px 14px', background: '#e67e22', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    {sending === g.fleet_nr ? 'Sending…' : '✉ Send reminder'}
                  </button>
                </div>
                {g.last_reminder && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    Last reminder {new Date(g.last_reminder.sent_at + 'Z').toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {' '}by {g.last_reminder.sent_by} to {g.last_reminder.recipients}
                  </div>
                )}
                {msg && <div style={{ fontSize: 12.5, marginTop: 6, color: msg.ok ? '#2e7d32' : '#c0392b' }}>{msg.text}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
